import { ExpandedTooltip, Icon } from '@livable/saas-react-components';
import { Button, MenuItem, Skeleton, TextField } from '@mui/material';
import React from 'react';
import { BILL_ALLOCATION_STATE as ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { formatExtractedMoney, formatUtilityLabel } from 'src/pages/NewBillingModule/ApplyBillingFull/BillAutomationExtraction';
import { AllocationSpikeReview } from './AllocationSpikeReview';
import { AllocationSuggestionEditForm } from './AllocationSuggestionEditForm';
import useStyles from './styles';
import { ALLOCATION_METHOD_LABELS, formatPercent, getOverlapMessage, getSkipMessage, hasOverlappingBills, isAwaitingSpikeReview } from './suggestionModel';
const SKELETON_ROWS = 4;
function SummaryItem(_ref) {
  let label = _ref.label,
    value = _ref.value;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.summaryItem
  }, /*#__PURE__*/React.createElement("span", null, label), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryValue
  }, value));
}
function StatusChip(_ref2) {
  let table = _ref2.table;
  const classes = useStyles();
  const _ref3 = (() => {
      if (table.isApproved) return {
        className: classes.statusChipDone,
        label: 'Allocated'
      };
      if (table.allocationState === ALLOCATION_STATE.allocating) return {
        className: '',
        label: 'Allocating…'
      };
      if (table.allocationState === ALLOCATION_STATE.awaitingApproval) return {
        className: '',
        label: 'Approving…'
      };
      if (isAwaitingSpikeReview(table)) return {
        className: classes.statusChipMuted,
        label: 'Needs review'
      };
      // Reviewed, it is waiting on approval like any other worked-out table.
      if (table.allocationState === ALLOCATION_STATE.needsReview) return {
        className: '',
        label: 'Pending approval'
      };
      if (table.allocationState === ALLOCATION_STATE.skipped) return {
        className: classes.statusChipMuted,
        label: 'Skipped'
      };
      if (table.allocationState === ALLOCATION_STATE.failed) return {
        className: classes.statusChipMuted,
        label: 'Could not allocate'
      };
      if (table.needsProperty || table.needsUtility) return {
        className: classes.statusChipMuted,
        label: 'Needs a match'
      };
      if (!table.isComputable) return {
        className: classes.statusChipMuted,
        label: 'Not allocated'
      };
      if (hasOverlappingBills(table)) return {
        className: classes.statusChipMuted,
        label: 'Already billed'
      };
      if (table.isLoading) return {
        className: '',
        label: 'Calculating…'
      };
      return {
        className: '',
        label: 'Pending approval'
      };
    })(),
    className = _ref3.className,
    label = _ref3.label;
  return /*#__PURE__*/React.createElement("span", {
    className: "".concat(classes.statusChip, " ").concat(className)
  }, label);
}

/** What the approve button offers, given where the service has got to. */
function getApproveState(table) {
  switch (table.allocationState) {
    case ALLOCATION_STATE.allocated:
      return {
        isDisabled: true,
        label: 'Allocated'
      };
    case ALLOCATION_STATE.allocating:
    case ALLOCATION_STATE.awaitingApproval:
      return {
        isDisabled: true,
        label: 'Allocating…'
      };
    case ALLOCATION_STATE.needsReview:
      // Reviewed, the hold is lifted and approving carries on where it left off.
      if (!isAwaitingSpikeReview(table)) return {
        isDisabled: false,
        label: 'Approve & allocate'
      };
      return {
        hint: 'This bill rose sharply on the last one. Review the comparison with the previous cycles, then confirm you have, to approve it.',
        isDisabled: true,
        label: 'Needs review'
      };
    case ALLOCATION_STATE.skipped:
      return {
        isDisabled: true,
        label: 'Skipped'
      };
    case ALLOCATION_STATE.failed:
      return {
        hint: table.allocationError,
        isDisabled: false,
        label: 'Try again'
      };
    default:
      return {
        isDisabled: false,
        label: 'Approve & allocate'
      };
  }
}
function getTargetMessage(table, utilityOptions) {
  if (table.needsProperty) {
    return 'The address on the bill does not match a single property on this company. Pick the property to allocate against.';
  }
  if (!utilityOptions.length) {
    return 'This property has no utilities set up, so there is nothing to allocate this service against. Set the utility up on the property, or pick another property.';
  }
  if (table.hasUtilityMatch) {
    return 'More than one utility on this property covers this service, so the bill does not say which one to allocate against.';
  }
  return 'No utility on this property matches this service, so pick the one to allocate against.';
}

/**
 * Shown when the bill cannot be tied to a single property utility on its own. The property stays
 * editable either way, because an address that matched the wrong property leads here too.
 */
function TargetPicker(_ref4) {
  let onSelectProperty = _ref4.onSelectProperty,
    onSelectUtility = _ref4.onSelectUtility,
    propertyOptions = _ref4.propertyOptions,
    table = _ref4.table,
    utilityOptions = _ref4.utilityOptions;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.picker
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.message
  }, getTargetMessage(table, utilityOptions)), /*#__PURE__*/React.createElement("div", {
    className: classes.pickerFields
  }, /*#__PURE__*/React.createElement(TextField, {
    className: classes.pickerField,
    label: "Property",
    onChange: event => onSelectProperty(event.target.value),
    select: true,
    size: "small",
    value: table.propertyId || ''
  }, propertyOptions.map(option => /*#__PURE__*/React.createElement(MenuItem, {
    key: option.id,
    value: option.id
  }, option.label))), table.needsProperty ? null : /*#__PURE__*/React.createElement(TextField, {
    className: classes.pickerField,
    disabled: !utilityOptions.length,
    label: "Utility",
    onChange: event => onSelectUtility(event.target.value),
    select: true,
    size: "small",
    value: table.utilityId || ''
  }, utilityOptions.map(option => /*#__PURE__*/React.createElement(MenuItem, {
    key: option.id,
    value: option.id
  }, option.label)))));
}
function UnitRows(_ref5) {
  let currency = _ref5.currency,
    units = _ref5.units;
  const classes = useStyles();
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.row, " ").concat(classes.rowHead)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.colUnit
  }, "Unit #"), /*#__PURE__*/React.createElement("span", {
    className: classes.colResident
  }, "Resident"), /*#__PURE__*/React.createElement("span", {
    className: classes.colOccupants
  }, "Occ."), /*#__PURE__*/React.createElement("span", {
    className: classes.colFactor
  }, "Occ. factor"), /*#__PURE__*/React.createElement("span", {
    className: classes.colDays
  }, "Days"), /*#__PURE__*/React.createElement("span", {
    className: classes.colTotal
  }, "Utility total")), units.map((unit, index) => /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.row, " ").concat(index === units.length - 1 ? classes.rowLast : '', " ").concat(unit.isBillable ? '' : classes.rowMuted),
    key: "".concat(unit.unit, "-").concat(unit.resident)
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.colUnit
  }, unit.unit), /*#__PURE__*/React.createElement("span", {
    className: classes.colResident,
    title: unit.resident
  }, unit.resident), /*#__PURE__*/React.createElement("span", {
    className: classes.colOccupants
  }, unit.occupants), /*#__PURE__*/React.createElement("span", {
    className: classes.colFactor
  }, Number(unit.occupancyFactor || 0).toFixed(2)), /*#__PURE__*/React.createElement("span", {
    className: classes.colDays
  }, unit.days), /*#__PURE__*/React.createElement("span", {
    className: classes.colTotal
  }, formatExtractedMoney(unit.total, currency)))));
}

/**
 * One suggested allocation table: what the allocation service would charge each unit for this
 * property and utility, before anything is saved. Shared by the Bills page and the billing cycle so
 * both show the same figures for the same bill values.
 */
export function AllocationSuggestionTable(_ref6) {
  let approveHint = _ref6.approveHint,
    currency = _ref6.currency,
    _ref6$isEditing = _ref6.isEditing,
    isEditing = _ref6$isEditing === void 0 ? false : _ref6$isEditing,
    onApprove = _ref6.onApprove,
    onChangeSetting = _ref6.onChangeSetting,
    onReview = _ref6.onReview,
    onSelectProperty = _ref6.onSelectProperty,
    onSelectUtility = _ref6.onSelectUtility,
    _ref6$propertyOptions = _ref6.propertyOptions,
    propertyOptions = _ref6$propertyOptions === void 0 ? [] : _ref6$propertyOptions,
    _ref6$setting = _ref6.setting,
    setting = _ref6$setting === void 0 ? {} : _ref6$setting,
    table = _ref6.table,
    _ref6$utilityOptions = _ref6.utilityOptions,
    utilityOptions = _ref6$utilityOptions === void 0 ? [] : _ref6$utilityOptions;
  const classes = useStyles();
  const needsTarget = table.needsProperty || table.needsUtility;
  // Once a service is allocated the figures are saved; editing them here would only disagree.
  const canEdit = isEditing && !needsTarget && !table.isAllocated;
  const hasUnits = (table.units || []).length > 0;
  const isAlreadyBilled = hasOverlappingBills(table);
  const approve = getApproveState(table);
  const renderBody = () => {
    if (needsTarget) {
      return /*#__PURE__*/React.createElement(TargetPicker, {
        onSelectProperty: onSelectProperty,
        onSelectUtility: onSelectUtility,
        propertyOptions: propertyOptions,
        table: table,
        utilityOptions: utilityOptions
      });
    }
    if (!table.isComputable) return /*#__PURE__*/React.createElement("div", {
      className: classes.message
    }, getSkipMessage(table.skipReason));
    if (table.hasFailed) {
      return /*#__PURE__*/React.createElement("div", {
        className: classes.message
      }, "The allocation suggestion could not be calculated. Please try again.");
    }
    if (hasUnits) return /*#__PURE__*/React.createElement(UnitRows, {
      currency: currency,
      units: table.units
    });
    if (table.isLoading) {
      return /*#__PURE__*/React.createElement(React.Fragment, null, Array.from({
        length: SKELETON_ROWS
      }).map((item, index) => /*#__PURE__*/React.createElement("div", {
        className: classes.skeletonRow,
        key: index
      }, /*#__PURE__*/React.createElement(Skeleton, {
        height: 20,
        variant: "text"
      }))));
    }
    return /*#__PURE__*/React.createElement("div", {
      className: classes.message
    }, "This property has no units to allocate against.");
  };
  return /*#__PURE__*/React.createElement("div", {
    className: classes.panel
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.titleRow
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.title
  }, formatUtilityLabel(table.serviceType), " allocation"), /*#__PURE__*/React.createElement(StatusChip, {
    table: table
  })), canEdit ? /*#__PURE__*/React.createElement(AllocationSuggestionEditForm, {
    currency: currency
    // The range picker holds its own dates, so switching tables has to remount it.
    ,
    key: table.key,
    onChange: onChangeSetting,
    setting: setting,
    table: table
  }) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.summary
  }, /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Service dates",
    value: table.servicePeriod
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Bill amount",
    value: formatExtractedMoney(table.billAmount, currency)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Deductions",
    value: formatPercent(table.deductionPercent)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Total billing",
    value: formatExtractedMoney(table.totalBilling, currency)
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Recovery",
    value: formatPercent(table.recoveryPercent)
  }), table.allocationMethod ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: classes.summaryDivider
  }), /*#__PURE__*/React.createElement(SummaryItem, {
    label: "Method",
    value: ALLOCATION_METHOD_LABELS[table.allocationMethod] || table.allocationMethod
  })) : null), needsTarget || !isAlreadyBilled ? null : /*#__PURE__*/React.createElement("div", {
    className: classes.warningBox
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.warningText
  }, getOverlapMessage(table.overlappingBills))), table.allocationError ? /*#__PURE__*/React.createElement("div", {
    className: classes.warningBox
  }, /*#__PURE__*/React.createElement(Icon, {
    state: "warning",
    type: "warning"
  }), /*#__PURE__*/React.createElement("span", {
    className: classes.warningText
  }, table.allocationError)) : null, isEditing || table.allocationState !== ALLOCATION_STATE.needsReview ? null : /*#__PURE__*/React.createElement(AllocationSpikeReview, {
    comparison: table.comparison,
    isSaving: !!table.isReviewing,
    onReview: onReview ? () => onReview(table.key) : undefined
  }), renderBody(), isEditing || !hasUnits ? null : /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, /*#__PURE__*/React.createElement(ExpandedTooltip, {
    placement: "top",
    title: (isAlreadyBilled ? 'These service dates are already billed for this utility.' : approve.hint) || approveHint || '',
    type: "grey"
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(Button, {
    disabled: !onApprove || approve.isDisabled || isAlreadyBilled,
    onClick: () => onApprove(table.key),
    size: "small",
    variant: "text"
  }, approve.label)))));
}